import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, ButtonVariant } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FC, useState } from 'react';
import { Loader } from 'components/Loader';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  onConfirm: () => void;
  variant?: 'default' | 'destructive';
  isLoading?: boolean;
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Continue',
  onConfirm,
  variant = 'default',
  isLoading = false,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-left text-text-light text-lg">{title}</AlertDialogTitle>
        </AlertDialogHeader>
        <p className="text-sm text-muted">{description}</p>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
              onConfirm();
            }}
            disabled={isLoading}
            className={cn(
              variant === 'destructive' && 'bg-red-600 hover:bg-red-700 focus:ring-red-600',
            )}
          >
            {isLoading ? <Loader h={4} w={4} color="text-white" /> : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

interface Props {
  openButtonText: string;
  headerText: string;
  bodyText: string;
  onConfirm: () => void;
  buttonVariant?: ButtonVariant;
  isLoading: boolean;
  disabled?: boolean;
}

export const ConfirmDialogButton: FC<Props> = ({
  openButtonText,
  onConfirm,
  headerText,
  bodyText,
  buttonVariant = 'default',
  isLoading = false,
  disabled = false,
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  return (
    <AlertDialog open={openDialog}>
      <AlertDialogTrigger asChild>
        <Button
          disabled={isLoading || disabled}
          variant={buttonVariant}
          onClick={() => setOpenDialog(true)}
          className={cn(buttonVariant === 'secondary' ? 'text-text-light' : 'text-white', 'w-full')}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader h={4} w={4} color="text-white" /> {openButtonText}
            </div>
          ) : (
            openButtonText
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-left text-text-light text-lg">
            {headerText}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <p className="text-sm text-muted">{bodyText}</p>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={(e) => {
              e.stopPropagation();
              setOpenDialog(false);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.stopPropagation();
              setOpenDialog(false);
              onConfirm();
            }}
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
